import i18n from "i18n"

export const PUBLISHING_FIELDS = {
  dsMarketplace: "publish_on_platform",
  frontEndVisibility: "publish_on_frontend_embed"
}

/**
 * Single source of truth for wizard steps.
 *
 * Each step declares:
 *   id      — unique step key, matches STEP_COMPONENTS and URL ?step= param
 *   label   — tab label
 *   title   — page heading (optional)
 *   subTitle — page sub-heading (optional)
 *   percent — progress bar value
 *   fields  — AntD form field names validated on step exit via form.validateFields().
 *             Steps whose data lives outside the form (media, location, etc.) use []
 *             and register custom handlers via stepSaveHandlersRef / stepValidateHandlersRef.
 *
 * Derive getSteps() and STEP_FIELDS from this array — never maintain a separate list.
 */
export const getStepConfig = (listingType) => {
  const isTicketed = listingType === "ticketed"

  return [
    {
      id: "details",
      label: i18n.t("listings.tabs.details.label"),
      title: isTicketed ? i18n.t("listings.tabs.details.title") : i18n.t("listings.tabs.details.private_title"),
      percent: 0,
      fields: [
        "title",
        "description",
        "categories",
        "sub_categories",
        ...(isTicketed ? ["min_guests", "max_guests"] : []),
        "languages",
        "skill_levels",
        "included_items",
        "bring_items",
        "accessibility_enabled",
        "alcohol_allowed",
        "children_allowed",
        PUBLISHING_FIELDS.frontEndVisibility,
        PUBLISHING_FIELDS.dsMarketplace
      ]
    },
    ...(isTicketed
      ? [
          {
            id: "ticket_type",
            label: "Ticket Type",
            title: "Ticket Type & Duration",
            percent: 15,
            fields: ["duration_type", "number_of_days", "min_tickets", "max_tickets", "ticket_description"]
          }
        ]
      : []),
    {
      id: "scheduling",
      label: i18n.t("listings.tabs.scheduling.label"),
      title: i18n.t("listings.tabs.scheduling.title"),
      percent: 30,
      fields: isTicketed
        ? ["duration_value", "duration_unit", "cut_off_value", "cut_off_unit"]
        : [
            "duration_type",
            "number_of_days",
            "cut_off_value",
            "cut_off_unit",
            "seasonal",
            "recurringYearly",
            "availability_schedules",
            "schedule_overrides"
          ]
    },
    {
      id: "pricing",
      label: "Pricing",
      title: "Pricing",
      subTitle: isTicketed
        ? "Prices are based on your base price and any adjustments you add."
        : undefined,
      percent: 40,
      fields: isTicketed
        ? []
        : ["pricing_type", "min_guests", "max_guests", "licenses_required"]
    },
    {
      id: "resources",
      label: i18n.t("listings.tabs.resources.label"),
      percent: 50,
      fields: []
    },
    // For both ticketed and non-ticketed: location comes before media.
    ...(isTicketed
      ? [
          {
            id: "location",
            label: i18n.t("listings.tabs.location.label"),
            title: i18n.t("listings.tabs.location.title"),
            subTitle: i18n.t("listings.tabs.location.subtitle"),
            percent: 55,
            fields: ["address"]
          }
        ]
      : []),
    // Location (non-ticketed only, since ticketed location is already above)
    ...(isTicketed
      ? []
      : [
          {
            id: "location",
            label: i18n.t("listings.tabs.location.label"),
            title: i18n.t("listings.tabs.location.title"),
            subTitle: i18n.t("listings.tabs.location.subtitle"),
            percent: 55,
            fields: ["address"]
          }
        ]),
    {
      id: "media",
      label: i18n.t("listings.tabs.media.label"),
      title: i18n.t("listings.tabs.media.title"),
      subTitle: i18n.t("listings.tabs.media.subtitle"),
      percent: 65,
      fields: []
    },
    {
      id: "additional_info",
      label: "Additional Info",
      title: "Additional Info",
      subTitle: "Documents & Questions, Add On's",
      percent: 88,
      fields: []
    },
    {
      id: "preview",
      label: i18n.t("listings.tabs.preview.label"),
      title: i18n.t("listings.tabs.preview.title"),
      subTitle: i18n.t("listings.tabs.preview.subtitle"),
      percent: 95,
      fields: []
    }
  ]
}

/**
 * Ordered list of step IDs — derived from getStepConfig so there is no second list to maintain.
 */
export const getSteps = (listingType) => getStepConfig(listingType).map((s) => s.id)

/**
 * Map of step ID → form field names — derived from getStepConfig.
 * Used by useExperienceWizard to run form.validateFields() on step exit.
 */
export const getStepFields = (listingType) =>
  Object.fromEntries(getStepConfig(listingType).map((s) => [s.id, s.fields]))
